import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { 
  Settings, Globe, Bell, CreditCard, Shield, Mail, 
  Save, Loader2, ToggleLeft, ToggleRight, DollarSign,
  Percent, Truck, Clock, CheckCircle2, Coins
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function PlatformSettings() {
  const { api } = useAuth();
  const { CURRENCIES, currencyConfig } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Platform Info
    platform_name: 'Hambre',
    support_email: 'support@hambre.app',
    support_phone: '+44 20 1234 5678',
    
    // Currency Settings
    default_currency: 'GBP',
    supported_currencies: ['GBP', 'EUR'],
    
    // Commission Settings
    delivery_commission_rate: 15,
    takeaway_commission_rate: 0,
    dine_in_commission_rate: 0,
    
    // Delivery Settings
    base_delivery_fee: 2.50,
    price_per_km: 0.50,
    free_delivery_threshold: 30,
    max_delivery_radius: 15,
    
    // App Service Fee
    app_service_fee: 0.50,
    
    // Driver Settings
    driver_base_pay: 3.00,
    driver_pay_per_km: 0.80,
    driver_min_payout: 50,
    
    // Feature Toggles
    enable_cash_payments: true,
    enable_card_payments: true,
    enable_wallet_payments: false,
    enable_table_booking: true,
    enable_takeaway: true,
    enable_delivery: true,
    
    // Automatic Payout Settings
    auto_restaurant_payout: true,
    auto_driver_payout: true,
    auto_salesperson_payout: false,
    
    // Notifications
    send_order_emails: true,
    send_marketing_emails: false,
    send_driver_notifications: true,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/platform-settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Use defaults if API fails
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/admin/platform-settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const Toggle = ({ enabled, onChange }) => (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-green-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Platform Settings</h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure global platform settings and features
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Platform Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-orange-500" />
              Platform Information
            </CardTitle>
            <CardDescription>Basic platform details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Platform Name</Label>
              <Input
                value={settings.platform_name}
                onChange={(e) => updateSetting('platform_name', e.target.value)}
                data-testid="platform-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Support Email</Label>
              <Input
                type="email"
                value={settings.support_email}
                onChange={(e) => updateSetting('support_email', e.target.value)}
                data-testid="support-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Support Phone</Label>
              <Input
                value={settings.support_phone}
                onChange={(e) => updateSetting('support_phone', e.target.value)}
                data-testid="support-phone"
              />
            </div>
          </CardContent>
        </Card>

        {/* Currency Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-orange-500" />
              Currency Settings
            </CardTitle>
            <CardDescription>Configure supported currencies for the platform</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Default Currency</Label>
              <select
                value={settings.default_currency}
                onChange={(e) => updateSetting('default_currency', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="default-currency"
              >
                <option value="GBP">£ GBP - British Pound</option>
                <option value="EUR">€ EUR - Euro</option>
              </select>
              <p className="text-xs text-slate-500">Default currency for new restaurants</p>
            </div>
            <div className="space-y-3">
              <Label>Supported Currencies</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={settings.supported_currencies?.includes('GBP')}
                    onChange={(e) => {
                      const newCurrencies = e.target.checked 
                        ? [...(settings.supported_currencies || []), 'GBP']
                        : (settings.supported_currencies || []).filter(c => c !== 'GBP');
                      updateSetting('supported_currencies', newCurrencies);
                    }}
                    className="w-4 h-4 text-orange-500"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-medium">£</span>
                    <div>
                      <p className="font-medium">British Pound (GBP)</p>
                      <p className="text-xs text-slate-500">United Kingdom</p>
                    </div>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={settings.supported_currencies?.includes('EUR')}
                    onChange={(e) => {
                      const newCurrencies = e.target.checked 
                        ? [...(settings.supported_currencies || []), 'EUR']
                        : (settings.supported_currencies || []).filter(c => c !== 'EUR');
                      updateSetting('supported_currencies', newCurrencies);
                    }}
                    className="w-4 h-4 text-orange-500"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-medium">€</span>
                    <div>
                      <p className="font-medium">Euro (EUR)</p>
                      <p className="text-xs text-slate-500">European Union countries</p>
                    </div>
                  </div>
                </label>
              </div>
              <p className="text-xs text-slate-500">Restaurants can choose from these currencies</p>
            </div>
          </CardContent>
        </Card>

        {/* Commission Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-orange-500" />
              Commission Rates
            </CardTitle>
            <CardDescription>Platform commission per order type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Delivery Orders (%)</Label>
              <Input
                type="number"
                min="0"
                max="50"
                value={settings.delivery_commission_rate}
                onChange={(e) => updateSetting('delivery_commission_rate', parseFloat(e.target.value))}
                data-testid="delivery-commission"
              />
              <p className="text-xs text-slate-500">Commission charged on delivery orders</p>
            </div>
            <div className="space-y-2">
              <Label>Takeaway Orders (%)</Label>
              <Input
                type="number"
                min="0"
                max="50"
                value={settings.takeaway_commission_rate}
                onChange={(e) => updateSetting('takeaway_commission_rate', parseFloat(e.target.value))}
                data-testid="takeaway-commission"
              />
              <p className="text-xs text-slate-500">Currently set to 0% (no commission)</p>
            </div>
            <div className="space-y-2">
              <Label>Dine-in Orders (%)</Label>
              <Input
                type="number"
                min="0"
                max="50"
                value={settings.dine_in_commission_rate}
                onChange={(e) => updateSetting('dine_in_commission_rate', parseFloat(e.target.value))}
                data-testid="dinein-commission"
              />
              <p className="text-xs text-slate-500">Currently set to 0% (no commission)</p>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              Delivery Settings
            </CardTitle>
            <CardDescription>Delivery fees and thresholds</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Base Delivery Fee (£)</Label>
              <Input
                type="number"
                step="0.50"
                min="0"
                value={settings.base_delivery_fee}
                onChange={(e) => updateSetting('base_delivery_fee', parseFloat(e.target.value))}
                data-testid="base-delivery-fee"
              />
            </div>
            <div className="space-y-2">
              <Label>Price Per KM (£)</Label>
              <Input
                type="number"
                step="0.10"
                min="0"
                value={settings.price_per_km}
                onChange={(e) => updateSetting('price_per_km', parseFloat(e.target.value))}
                data-testid="price-per-km"
              />
            </div>
            <div className="space-y-2">
              <Label>Free Delivery Threshold (£)</Label>
              <Input
                type="number"
                min="0"
                value={settings.free_delivery_threshold}
                onChange={(e) => updateSetting('free_delivery_threshold', parseFloat(e.target.value))}
                data-testid="free-delivery-threshold"
              />
              <p className="text-xs text-slate-500">Orders above this get free delivery</p>
            </div>
            <div className="space-y-2">
              <Label>Max Delivery Radius (KM)</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={settings.max_delivery_radius}
                onChange={(e) => updateSetting('max_delivery_radius', parseFloat(e.target.value))}
                data-testid="max-radius"
              />
            </div>
            <div className="space-y-2">
              <Label>App Service Fee (£)</Label>
              <Input
                type="number"
                step="0.10"
                min="0"
                value={settings.app_service_fee}
                onChange={(e) => updateSetting('app_service_fee', parseFloat(e.target.value))}
                data-testid="app-service-fee"
              />
              <p className="text-xs text-slate-500">Fixed fee added to delivery orders</p>
            </div>
          </CardContent>
        </Card>

        {/* Driver Pay Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-orange-500" />
              Driver Pay Settings
            </CardTitle>
            <CardDescription>Configure driver earnings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Base Pay Per Delivery (£)</Label>
              <Input
                type="number"
                step="0.50"
                min="0"
                value={settings.driver_base_pay}
                onChange={(e) => updateSetting('driver_base_pay', parseFloat(e.target.value))}
                data-testid="driver-base-pay"
              />
            </div>
            <div className="space-y-2">
              <Label>Pay Per KM (£)</Label>
              <Input
                type="number"
                step="0.10"
                min="0"
                value={settings.driver_pay_per_km}
                onChange={(e) => updateSetting('driver_pay_per_km', parseFloat(e.target.value))}
                data-testid="driver-pay-per-km"
              />
            </div>
            <div className="space-y-2">
              <Label>Minimum Payout Amount (£)</Label>
              <Input
                type="number"
                min="0"
                value={settings.driver_min_payout}
                onChange={(e) => updateSetting('driver_min_payout', parseFloat(e.target.value))}
                data-testid="driver-min-payout"
              />
              <p className="text-xs text-slate-500">Minimum balance required for payout</p>
            </div>
          </CardContent>
        </Card>

        {/* Feature Toggles */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-500" />
              Feature Toggles
            </CardTitle>
            <CardDescription>Enable or disable platform features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Delivery Orders</Label>
                <p className="text-xs text-slate-500">Allow customers to order delivery</p>
              </div>
              <Toggle
                enabled={settings.enable_delivery}
                onChange={(val) => updateSetting('enable_delivery', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Takeaway Orders</Label>
                <p className="text-xs text-slate-500">Allow customers to order takeaway</p>
              </div>
              <Toggle
                enabled={settings.enable_takeaway}
                onChange={(val) => updateSetting('enable_takeaway', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Table Booking</Label>
                <p className="text-xs text-slate-500">Allow table reservations</p>
              </div>
              <Toggle
                enabled={settings.enable_table_booking}
                onChange={(val) => updateSetting('enable_table_booking', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Cash Payments</Label>
                <p className="text-xs text-slate-500">Allow cash on delivery</p>
              </div>
              <Toggle
                enabled={settings.enable_cash_payments}
                onChange={(val) => updateSetting('enable_cash_payments', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Card Payments</Label>
                <p className="text-xs text-slate-500">Allow card payments via Stripe</p>
              </div>
              <Toggle
                enabled={settings.enable_card_payments}
                onChange={(val) => updateSetting('enable_card_payments', val)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Automatic Payout Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-500" />
              Automatic Payouts
            </CardTitle>
            <CardDescription>Configure automatic payout settings when orders are delivered</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg mb-4">
              <p className="text-sm text-blue-700">
                When enabled, payouts are automatically transferred to connected Stripe accounts 
                immediately when an order is marked as delivered.
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Pay Restaurants</Label>
                <p className="text-xs text-slate-500">Automatically transfer order earnings to restaurants</p>
              </div>
              <Toggle
                enabled={settings.auto_restaurant_payout}
                onChange={(val) => updateSetting('auto_restaurant_payout', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Pay Drivers</Label>
                <p className="text-xs text-slate-500">Automatically transfer delivery fees to drivers</p>
              </div>
              <Toggle
                enabled={settings.auto_driver_payout}
                onChange={(val) => updateSetting('auto_driver_payout', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Pay Salesperson Commissions</Label>
                <p className="text-xs text-slate-500">Instantly pay salesperson commissions per order (otherwise they accumulate)</p>
              </div>
              <Toggle
                enabled={settings.auto_salesperson_payout}
                onChange={(val) => updateSetting('auto_salesperson_payout', val)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Notifications
            </CardTitle>
            <CardDescription>Configure notification settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Order Emails</Label>
                <p className="text-xs text-slate-500">Send order confirmations</p>
              </div>
              <Toggle
                enabled={settings.send_order_emails}
                onChange={(val) => updateSetting('send_order_emails', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Marketing Emails</Label>
                <p className="text-xs text-slate-500">Send promotional emails</p>
              </div>
              <Toggle
                enabled={settings.send_marketing_emails}
                onChange={(val) => updateSetting('send_marketing_emails', val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Driver Notifications</Label>
                <p className="text-xs text-slate-500">Push notifications to drivers</p>
              </div>
              <Toggle
                enabled={settings.send_driver_notifications}
                onChange={(val) => updateSetting('send_driver_notifications', val)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button (bottom) */}
      <div className="flex justify-end pt-4 border-t">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mr-2" />
          )}
          Save All Changes
        </Button>
      </div>
    </div>
  );
}
